import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { User, VoicePreset, PresetAudioReview, SkillStatus } from '../types';
import { listVoicePresets, createVoicePreset, updateVoicePreset, deleteVoicePreset, submitVoicePreset, withdrawVoicePreset, approveVoicePreset, rejectVoicePreset, publishVoicePreset, unpublishVoicePreset, uploadPresetAudio, listPresetAudioReviews, resubmitPresetAudioReview } from '../api';
import AudioPlayer from '../components/AudioPlayer';
import Breadcrumb from '../components/Breadcrumb';
import Modal from '../components/Modal';
import ConfirmDialog from '../components/ConfirmDialog';
import { useToast } from '../components/Toast';
import { hasPermission } from '../utils/permissions';

const emptyForm = { name: '', prompt: '', isDefault: false };

// 前端仅允许 mp3 / wav（accept 只是选择器提示，需在选中时硬校验）
const isAllowedAudioFile = (file: File): boolean => {
  const name = file.name.toLowerCase();
  return name.endsWith('.mp3') || name.endsWith('.wav');
};

const statusLabels: Record<SkillStatus, { text: string; color: string; bg: string }> = {
  draft: { text: 'draft', color: 'var(--text-2)', bg: 'var(--surface-2)' },
  pending_review: { text: 'pending_review', color: '#b45309', bg: '#fef3c7' },
  published: { text: 'published', color: '#15803d', bg: '#dcfce7' },
  unpublished: { text: 'unpublished', color: '#dc2626', bg: '#fee2e2' },
};

// 每平台预审状态徽标（沿用 statusLabels 的配色规范）
const reviewStatusLabels: Record<PresetAudioReview['status'], { color: string; bg: string }> = {
  pending: { color: 'var(--text-2)', bg: 'var(--surface-2)' },
  processing: { color: '#b45309', bg: '#fef3c7' },
  active: { color: '#15803d', bg: '#dcfce7' },
  failed: { color: '#dc2626', bg: '#fee2e2' },
};

const VoicePresetList: React.FC = () => {
  const { t } = useTranslation('skills');
  const toast = useToast();
  const currentUser: User | null = JSON.parse(localStorage.getItem('user') || 'null');
  const canModerate = hasPermission(currentUser, 'operations');       // 审核/查看全部/管理员视图/published
  const canEditDefault = hasPermission(currentUser, 'platform_config'); // default 资产编辑
  const canEdit = (preset: VoicePreset) => canModerate || preset.createdBy === currentUser?.id;
  const isOwner = (preset: VoicePreset) => !!currentUser && preset.createdBy === currentUser.id;
  const [presets, setPresets] = useState<VoicePreset[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingPreset, setEditingPreset] = useState<VoicePreset | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<VoicePreset | null>(null);

  // 每个预设当前音频的每平台预审状态（仅管理员可见，卡片只读展示）
  const [audioReviews, setAudioReviews] = useState<Record<string, PresetAudioReview[]>>({});
  // 新建/编辑弹窗里选择的音频文件（保存预设后再上传并触发各平台预审）
  const [audioFile, setAudioFile] = useState<File | null>(null);

  // reject modal
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectTarget, setRejectTarget] = useState<VoicePreset | null>(null);
  const [rejectNote, setRejectNote] = useState('');

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = async () => {
    try {
      setLoading(true);
      const data = await listVoicePresets();
      const list = data || [];
      setPresets(list);
      if (canModerate) {
        const entries = await Promise.all(
          list
            .filter(p => p.audioUrl)
            .map(async (p): Promise<[string, PresetAudioReview[]]> => {
              try {
                return [p.id, await listPresetAudioReviews(p.id)];
              } catch {
                // 单个预设状态拉取失败不应阻断整页（尽力而为展示）
                return [p.id, []];
              }
            }),
        );
        setAudioReviews(Object.fromEntries(entries));
      }
    } finally {
      setLoading(false);
    }
  };

  const refreshReviews = async (presetId: string) => {
    try {
      const reviews = await listPresetAudioReviews(presetId);
      setAudioReviews(prev => ({ ...prev, [presetId]: reviews }));
    } catch {
      // 状态刷新失败不弹错，保留上次结果
    }
  };

  const handleResubmitReview = async (preset: VoicePreset, platform: string) => {
    try {
      await resubmitPresetAudioReview(preset.id, platform);
      toast.success(t('voicePreset.audio.resubmitSuccess'));
      await refreshReviews(preset.id);
    } catch (err: any) {
      toast.error(err.message || t('common.operationFailed'));
    }
  };

  const openCreate = () => {
    setEditingPreset(null);
    setForm(emptyForm);
    setAudioFile(null);
    setError('');
    setShowModal(true);
  };

  const openEdit = (preset: VoicePreset) => {
    setEditingPreset(preset);
    setForm({ name: preset.name, prompt: preset.prompt, isDefault: preset.isDefault });
    setAudioFile(null);
    setError('');
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) { setError(t('common.nameRequired')); return; }
    if (!form.prompt.trim()) { setError(t('voicePreset.promptRequired')); return; }
    setSaving(true);
    setError('');
    try {
      let target: VoicePreset;
      if (editingPreset) {
        target = await updateVoicePreset(editingPreset.id, form);
        setPresets(prev => prev.map(p => (p.id === target.id ? target : p)));
      } else {
        target = await createVoicePreset(form);
        setPresets(prev => [...prev, target]);
      }
      // 管理员在弹窗里选了音频：保存预设后上传，自动触发各真人平台预审
      if (audioFile && canModerate) {
        try {
          const { audioUrl } = await uploadPresetAudio(target.id, audioFile);
          setPresets(prev => prev.map(p => (p.id === target.id ? { ...p, audioUrl } : p)));
          await refreshReviews(target.id);
          toast.success(t('voicePreset.audio.uploadSuccess'));
        } catch (err: any) {
          // 预设已保存成功，仅音频上传失败：提示但不阻断
          toast.error(err.message || t('common.uploadFailed'));
        }
      }
      setShowModal(false);
    } catch (err: any) {
      setError(err.message || t('common.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (preset: VoicePreset) => {
    setDeleteTarget(preset);
    setConfirmDelete(true);
  };

  const doDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteVoicePreset(deleteTarget.id);
      setPresets(prev => prev.filter(p => p.id !== deleteTarget.id));
    } catch (err: any) {
      toast.error(err.message || t('common.deleteFailed'));
    } finally {
      setConfirmDelete(false);
      setDeleteTarget(null);
    }
  };

  const handleSubmit = async (preset: VoicePreset) => {
    try {
      const updated = await submitVoicePreset(preset.id);
      setPresets(prev => prev.map(p => p.id === preset.id ? updated : p));
      toast.success(t('common.submitReviewSuccess'));
    } catch (err: any) {
      toast.error(err.message || t('common.submitFailed'));
    }
  };

  const handleWithdraw = async (preset: VoicePreset) => {
    try {
      const updated = await withdrawVoicePreset(preset.id);
      setPresets(prev => prev.map(p => p.id === preset.id ? updated : p));
      toast.success(t('common.withdrawSuccess'));
    } catch (err: any) {
      toast.error(err.message || t('common.withdrawFailed'));
    }
  };

  const handleApprove = async (preset: VoicePreset) => {
    try {
      const updated = await approveVoicePreset(preset.id);
      setPresets(prev => prev.map(p => p.id === preset.id ? updated : p));
      toast.success(t('common.approveSuccess'));
    } catch (err: any) {
      toast.error(err.message || t('common.operationFailed'));
    }
  };

  const openReject = (preset: VoicePreset) => {
    setRejectTarget(preset);
    setRejectNote('');
    setShowRejectModal(true);
  };

  const doReject = async () => {
    if (!rejectTarget) return;
    if (!rejectNote.trim()) { toast.warning(t('common.rejectNoteRequired')); return; }
    try {
      const updated = await rejectVoicePreset(rejectTarget.id, rejectNote);
      setPresets(prev => prev.map(p => p.id === rejectTarget.id ? updated : p));
      toast.success(t('common.rejected'));
      setShowRejectModal(false);
    } catch (err: any) {
      toast.error(err.message || t('common.operationFailed'));
    }
  };

  const handlePublish = async (preset: VoicePreset) => {
    try {
      const updated = await publishVoicePreset(preset.id);
      setPresets(prev => prev.map(p => p.id === preset.id ? updated : p));
      toast.success(t('common.republished'));
    } catch (err: any) {
      toast.error(err.message || t('common.operationFailed'));
    }
  };

  const handleUnpublish = async (preset: VoicePreset) => {
    try {
      const updated = await unpublishVoicePreset(preset.id);
      setPresets(prev => prev.map(p => p.id === preset.id ? updated : p));
      toast.success(t('common.unpublishedSuccess'));
    } catch (err: any) {
      toast.error(err.message || t('common.operationFailed'));
    }
  };

  const getStatus = (preset: VoicePreset): SkillStatus => preset.status || 'published';

  return (
    <div className="container" style={{ maxWidth: '900px', margin: '0 auto', padding: '2rem 1rem' }}>
      <Breadcrumb items={[{ label: t('common.breadcrumbHome'), to: '/projects' }, { label: t('voicePreset.breadcrumbTitle') }]} />
      <div className="page-header">
        <h1 className="page-title">{t('voicePreset.pageTitle')}</h1>
        <button className="btn btn-primary" onClick={openCreate}>{t('voicePreset.createBtn')}</button>
      </div>

      {loading ? (
        <p>{t('common.loading')}</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {presets.length === 0 && <p style={{ color: 'var(--text-2)' }}>{t('voicePreset.empty')}</p>}
          {presets.map(preset => {
            const status = getStatus(preset);
            const label = statusLabels[status];
            return (
            <div key={preset.id} className="list-item" style={{ alignItems: 'flex-start' }}>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem', flexWrap: 'wrap' }}>
                  <span style={{ fontWeight: 600, fontSize: '1rem' }}>{preset.name}</span>
                  {preset.isDefault && <span style={{ background: 'var(--primary)', color: '#fff', fontSize: '0.7rem', padding: '1px 6px', borderRadius: '10px' }}>{t('common.defaultBadge')}</span>}
                  <span style={{ fontSize: '0.7rem', padding: '1px 8px', borderRadius: '10px', color: label.color, background: label.bg }}>{t('status.' + label.text)}</span>
                </div>
                {preset.reviewNote && status === 'draft' && (
                  <p style={{ margin: '0 0 0.5rem 0', fontSize: '0.8rem', color: '#dc2626', background: '#fee2e2', padding: '0.3rem 0.5rem', borderRadius: '4px' }}>
                    {t('common.rejectReason')}{preset.reviewNote}
                  </p>
                )}
                <p style={{ margin: 0, fontSize: '0.85rem', color: 'var(--text-2)' }}>{preset.prompt}</p>
                {canModerate && (
                  <div style={{ marginTop: '0.75rem', paddingTop: '0.75rem', borderTop: '1px solid var(--border)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
                      <span style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-2)' }}>{t('voicePreset.audio.sectionTitle')}</span>
                      {preset.audioUrl ? (
                        <AudioPlayer src={preset.audioUrl} maxWidth={300} />
                      ) : (
                        <span style={{ fontSize: '0.8rem', color: 'var(--text-2)' }}>{t('voicePreset.audio.none')}</span>
                      )}
                    </div>
                    {(audioReviews[preset.id]?.length ?? 0) > 0 && (
                      <div style={{ marginTop: '0.5rem' }}>
                        <div style={{ fontSize: '0.72rem', color: 'var(--text-2)', marginBottom: '0.35rem' }}>{t('voicePreset.audio.reviewTitle')}</div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
                          {audioReviews[preset.id].map(review => {
                            const reviewLabel = reviewStatusLabels[review.status];
                            return (
                              <div key={review.platform} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap', fontSize: '0.8rem' }}>
                                <span style={{ fontWeight: 600, minWidth: '64px' }}>{review.platform}</span>
                                <span style={{ fontSize: '0.7rem', padding: '1px 8px', borderRadius: '10px', color: reviewLabel.color, background: reviewLabel.bg }}>
                                  {t('voicePreset.audio.status.' + review.status)}
                                </span>
                                {review.assetId && (
                                  <span style={{ color: 'var(--text-2)', fontFamily: 'monospace', fontSize: '0.72rem' }}>{review.assetId}</span>
                                )}
                                {review.status === 'failed' && (
                                  <>
                                    {review.errorMsg && <span style={{ color: '#dc2626', fontSize: '0.72rem' }}>{review.errorMsg}</span>}
                                    <button
                                      className="btn btn-ghost"
                                      style={{ fontSize: '0.72rem', padding: '0.15rem 0.5rem' }}
                                      onClick={() => handleResubmitReview(preset, review.platform)}
                                    >
                                      {t('voicePreset.audio.resubmit')}
                                    </button>
                                  </>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div className="list-item-actions" style={{ display: 'flex', gap: '0.5rem', flexShrink: 0, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                {/* 创建者：草稿或已下架可提交审核 */}
                {isOwner(preset) && (status === 'draft' || status === 'unpublished') && (
                  <button className="btn btn-primary" style={{ fontSize: '0.8rem', padding: '0.3rem 0.75rem' }} onClick={() => handleSubmit(preset)}>{t('common.submitReview')}</button>
                )}
                {/* 创建者：审核中可撤回 */}
                {isOwner(preset) && status === 'pending_review' && (
                  <button className="btn btn-ghost" style={{ fontSize: '0.8rem', padding: '0.3rem 0.75rem' }} onClick={() => handleWithdraw(preset)}>{t('common.withdraw')}</button>
                )}
                {/* 管理员：审核中可通过/驳回 */}
                {canModerate && status === 'pending_review' && (
                  <>
                    <button className="btn btn-primary" style={{ fontSize: '0.8rem', padding: '0.3rem 0.75rem' }} onClick={() => handleApprove(preset)}>{t('common.approve')}</button>
                    <button className="btn btn-danger" style={{ fontSize: '0.8rem', padding: '0.3rem 0.75rem' }} onClick={() => openReject(preset)}>{t('common.reject')}</button>
                  </>
                )}
                {/* 管理员：已上架可下架 */}
                {canModerate && status === 'published' && !preset.isDefault && (
                  <button className="btn btn-ghost" style={{ fontSize: '0.8rem', padding: '0.3rem 0.75rem', color: '#dc2626' }} onClick={() => handleUnpublish(preset)}>{t('common.unpublish')}</button>
                )}
                {/* 管理员：草稿/审核中/已下架均可直接上架（无需用户提交审核） */}
                {canModerate && (status === 'draft' || status === 'pending_review' || status === 'unpublished') && (
                  <button className="btn btn-primary" style={{ fontSize: '0.8rem', padding: '0.3rem 0.75rem' }} onClick={() => handlePublish(preset)}>
                    {status === 'unpublished' ? t('common.republish') : t('common.publish')}
                  </button>
                )}
                {canEdit(preset) && (status === 'draft' || status === 'unpublished' || canModerate) && (
                  <button className="btn btn-ghost" style={{ fontSize: '0.8rem', padding: '0.3rem 0.75rem' }} onClick={() => openEdit(preset)}>{t('common.edit')}</button>
                )}
                {canEdit(preset) && (status !== 'published' || canModerate) && (
                  <button className="btn btn-danger" style={{ fontSize: '0.8rem', padding: '0.3rem 0.75rem' }} onClick={() => handleDelete(preset)} disabled={preset.isDefault}>{t('common.delete')}</button>
                )}
              </div>
            </div>
            );
          })}
        </div>
      )}

      <Modal
        open={showModal}
        onClose={() => setShowModal(false)}
        title={editingPreset ? t('voicePreset.editTitle') : t('voicePreset.createTitle')}
        width="500px"
        footer={
          <>
            <button className="btn btn-secondary" onClick={() => setShowModal(false)} disabled={saving}>{t('common.cancel')}</button>
            <button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving ? t('common.saving') : t('common.save')}</button>
          </>
        }
      >
        <div className="form-group">
          <label className="form-label">{t('voicePreset.nameLabel')}</label>
          <input className="input" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder={t('voicePreset.namePlaceholder')} />
        </div>
        <div className="form-group">
          <label className="form-label">{t('voicePreset.promptLabel')}</label>
          <textarea
            className="input textarea"
            style={{ minHeight: '100px' }}
            value={form.prompt}
            onChange={e => setForm({ ...form, prompt: e.target.value })}
            placeholder={t('voicePreset.promptPlaceholder')}
          />
          <p style={{ margin: '0.25rem 0 0', fontSize: '0.75rem', color: 'var(--text-2)' }}>
            {t('voicePreset.promptHint')}
          </p>
        </div>
        {canEditDefault && (
        <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <input type="checkbox" id="voiceIsDefault" checked={form.isDefault} onChange={e => setForm({ ...form, isDefault: e.target.checked })} />
          <label htmlFor="voiceIsDefault" style={{ margin: 0, cursor: 'pointer' }}>{t('voicePreset.setDefaultPreset')}</label>
        </div>
        )}
        {canModerate && (
          <div className="form-group">
            <label className="form-label">{t('voicePreset.audio.sectionTitle')}</label>
            {editingPreset?.audioUrl && !audioFile && (
              <div style={{ marginBottom: '0.5rem' }}>
                <AudioPlayer src={editingPreset.audioUrl} maxWidth="100%" />
              </div>
            )}
            <label
              className="btn btn-ghost"
              style={{ fontSize: '0.85rem', padding: '0.35rem 0.85rem', margin: 0, cursor: 'pointer' }}
            >
              {audioFile
                ? audioFile.name
                : editingPreset?.audioUrl
                  ? t('voicePreset.audio.replace')
                  : t('voicePreset.audio.upload')}
              <input
                type="file"
                accept=".mp3,.wav,audio/mpeg,audio/wav,audio/x-wav"
                style={{ display: 'none' }}
                onChange={e => {
                  const file = e.target.files?.[0];
                  e.target.value = '';
                  if (!file) return;
                  if (!isAllowedAudioFile(file)) {
                    toast.error(t('voicePreset.audio.invalidType'));
                    return;
                  }
                  setAudioFile(file);
                }}
              />
            </label>
            <p style={{ margin: '0.25rem 0 0', fontSize: '0.75rem', color: 'var(--text-2)' }}>
              {t('voicePreset.audio.reviewOnSaveHint')}
            </p>
          </div>
        )}
        {error && <p className="error-text">{error}</p>}
      </Modal>

      <ConfirmDialog
        open={confirmDelete}
        onCancel={() => { setConfirmDelete(false); setDeleteTarget(null); }}
        onConfirm={doDelete}
        title={t('voicePreset.deleteTitle')}
        message={<>{t('voicePreset.deleteConfirm', { name: '' })}<strong>{deleteTarget?.name}</strong></>}
        confirmText={t('common.confirmDelete')}
        confirmDanger
      />

      {/* 驳回弹窗 */}
      <Modal
        open={showRejectModal}
        onClose={() => setShowRejectModal(false)}
        title={t('voicePreset.rejectTitle')}
        width="450px"
        footer={
          <>
            <button className="btn btn-secondary" onClick={() => setShowRejectModal(false)}>{t('common.cancel')}</button>
            <button className="btn btn-danger" onClick={doReject}>{t('common.confirmReject')}</button>
          </>
        }
      >
        <p style={{ marginTop: 0, fontSize: '0.85rem' }}>{t('voicePreset.rejectPrompt', { name: '' })}<strong>{rejectTarget?.name}</strong></p>
        <textarea
          className="input textarea"
          style={{ minHeight: '80px', width: '100%', boxSizing: 'border-box' }}
          value={rejectNote}
          onChange={e => setRejectNote(e.target.value)}
          placeholder={t('voicePreset.rejectPlaceholder')}
        />
      </Modal>
    </div>
  );
};

export default VoicePresetList;
